import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import MainButton from '../../../shared/layout/buttons/MainButton';
import AdminDataTable from './components/AdminDataTable';
import { fetchAdminVenue, fetchAdminVenueIntegration, updateAdminVenue, updateAdminVenueIntegration } from '../../../services/Http/AdminApi';
import CitySearchSelect from '../../global/CitySearchSelect';

const AdminVenueDetail = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [venue, setVenue] = useState(null);
  const [integration, setIntegration] = useState(null);
  const [form, setForm] = useState({ is_api_active: false, website: '' });
  const [profileForm, setProfileForm] = useState({
    name: '',
    description: '',
    email: '',
    phone: '',
    address: '',
    website: '',
    country_id: '',
    city_id: '',
    city: '',
  });
  const [saving, setSaving] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [venueResp, integrationResp] = await Promise.all([
        fetchAdminVenue(id),
        fetchAdminVenueIntegration(id),
      ]);
      const venueData = venueResp?.data || null;
      setVenue({ data: venueData, meta: venueResp?.meta || {} });
      setIntegration(integrationResp?.data || null);
      setForm({
        is_api_active: Boolean(integrationResp?.data?.venue?.is_api_active),
        website: venueData?.website || '',
      });
      setProfileForm({
        name: venueData?.name || '',
        description: venueData?.description || '',
        email: venueData?.email || '',
        phone: venueData?.phone || '',
        address: venueData?.address || '',
        website: venueData?.website || '',
        country_id: venueData?.country_id || '',
        city_id: venueData?.city_id || '',
        city: venueData?.city || '',
      });
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to load venue detail');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const saveIntegration = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateAdminVenueIntegration(id, form);
      toast.success('Venue integration settings updated');
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update venue integration');
    } finally {
      setSaving(false);
    }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await updateAdminVenue(id, profileForm);
      toast.success('Venue profile updated');
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update venue');
    } finally {
      setSavingProfile(false);
    }
  };

  return (
    <PageContainer>
      <PageHeader><PageTitle>Venue Detail</PageTitle></PageHeader>
      <PageContent>
        <Toolbar>
          <Link to="/venues">Back to venues</Link>
          <MainButton size="small" onClick={load}>Refresh</MainButton>
        </Toolbar>

        {loading ? <Panel>Loading...</Panel> : !venue?.data ? <Panel>Venue not found</Panel> : (
          <>
            <Grid>
              <Panel>
                <h3>Venue Profile</h3>
                <Row><span>ID</span><code>{venue.data.id}</code></Row>
                <Row><span>Organization</span><span>{venue.data.organization_name || '—'}</span></Row>
                <Row><span>Virtual Source</span><span>{venue.data.virtual_source || 'real'}</span></Row>
                <Form onSubmit={saveProfile}>
                  <Input
                    value={profileForm.name}
                    onChange={(e) => setProfileForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="Venue name"
                  />
                  <Input
                    value={profileForm.address}
                    onChange={(e) => setProfileForm((f) => ({ ...f, address: e.target.value }))}
                    placeholder="Address"
                  />
                  <CitySearchSelect
                    countryId={profileForm.country_id || null}
                    cityId={profileForm.city_id || null}
                    value={profileForm.city}
                    useAdminApi
                    placeholder="City"
                    onChange={(city) =>
                      setProfileForm((f) => ({
                        ...f,
                        city: city?.name || '',
                        city_id: city?.id || '',
                      }))
                    }
                  />
                  <Input
                    value={profileForm.email}
                    onChange={(e) => setProfileForm((f) => ({ ...f, email: e.target.value }))}
                    placeholder="Email"
                  />
                  <Input
                    value={profileForm.phone}
                    onChange={(e) => setProfileForm((f) => ({ ...f, phone: e.target.value }))}
                    placeholder="Phone"
                  />
                  <Input
                    value={profileForm.website}
                    onChange={(e) => setProfileForm((f) => ({ ...f, website: e.target.value }))}
                    placeholder="Website"
                  />
                  <TextArea
                    value={profileForm.description}
                    onChange={(e) => setProfileForm((f) => ({ ...f, description: e.target.value }))}
                    placeholder="Description"
                    rows={3}
                  />
                  <Actions>
                    <MainButton type="submit" disabled={savingProfile}>
                      {savingProfile ? 'Saving...' : 'Save venue profile'}
                    </MainButton>
                  </Actions>
                </Form>
              </Panel>

              <Panel>
                <h3>Integration Settings</h3>
                <Form onSubmit={saveIntegration}>
                  <Label>
                    <input type="checkbox" checked={!!form.is_api_active} onChange={(e) => setForm((f) => ({ ...f, is_api_active: e.target.checked }))} />
                    <span>API active</span>
                  </Label>
                  <Input value={form.website} onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))} placeholder="Website" />
                  <Actions>
                    <MainButton type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save integration settings'}</MainButton>
                  </Actions>
                </Form>
                <Subtle>Changes are audited on backend (`venue.integration.update`).</Subtle>
              </Panel>
            </Grid>

            <Section>
              <h3>Linked Integrators</h3>
              <AdminDataTable
                rows={integration?.integrators || []}
                loading={loading}
                emptyText="No integrators linked to this venue"
                columns={[
                  { key: 'name', label: 'Integrator' },
                  { key: 'integration_type', label: 'Type' },
                  { key: 'is_active', label: 'Active', render: (r) => (r.is_active ? 'Yes' : 'No') },
                  { key: 'last_sync_at', label: 'Last Sync', render: (r) => r.last_sync_at ? new Date(r.last_sync_at).toLocaleString() : '—' },
                  { key: 'updated_at', label: 'Updated', render: (r) => r.updated_at ? new Date(r.updated_at).toLocaleString() : '—' },
                ]}
              />
            </Section>
          </>
        )}
      </PageContent>
    </PageContainer>
  );
};

export default AdminVenueDetail;

const Toolbar = styled.div`display:flex;justify-content:space-between;align-items:center;gap: 0.75rem;margin-bottom: 1rem; a{color:var(--color-primary);text-decoration:none;font-weight:600;}`;
const Grid = styled.div`display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap: 1rem; @media (max-width: 900px){grid-template-columns:1fr;}`;
const Panel = styled.div`border:1px solid ${({theme})=>theme.colors.border};background:${({theme})=>theme.colors.surface};border-radius:12px;padding: 1rem; h3{margin: 0 0 0.75rem;}`;
const Row = styled.div`display:grid;grid-template-columns:140px 1fr;gap: 0.625rem;padding: 0.5rem 0;border-bottom:1px solid ${({theme})=>theme.colors.border}; &:last-child{border-bottom:none;} span:first-child{color:${({theme})=>theme.colors.textMuted};font-weight:600;} code{font-size: 0.75rem;word-break:break-all;}`;
const Form = styled.form`display:flex;flex-direction:column;gap: 0.75rem;`;
const Input = styled.input`border:1px solid ${({theme})=>theme.colors.border};border-radius:10px;padding: 0.625rem 0.75rem;background:${({theme})=>theme.colors.surface};`;
const TextArea = styled.textarea`border:1px solid ${({theme})=>theme.colors.border};border-radius:10px;padding: 0.625rem 0.75rem;background:${({theme})=>theme.colors.surface};resize:vertical;min-height:88px;`;
const Label = styled.label`display:flex;align-items:center;gap: 0.5rem;font-weight:600;color:${({theme})=>theme.colors.textPrimary};`;
const Actions = styled.div`display:flex;justify-content:flex-start;`;
const Subtle = styled.p`margin: 0.625rem 0 0;color:${({theme})=>theme.colors.textMuted};font-size: 0.75rem;`;
const Section = styled.section`margin-top: 1.25rem; h3{margin: 0 0 0.75rem;color:${({theme})=>theme.colors.textPrimary};}`;
